import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import { TCatalogEntities } from "types";
import { VehicleStore } from "stores";

import List from "../../List";
import Filter from "../../Filter";

import Pipeline from "../Pipeline";
import { sortAsc } from "../helper";

import FzartReducer from "./FzartReducer";
import FzartIndicator from "./FzartIndicator";

interface IFilter {
  entities: TCatalogEntities;
  value: number[];
  dispatchFilter: any;
}

export const SideFilter: React.FC<IFilter> = ({
  entities = { fzart: { "1": { id: 1, name: "Option 1" } } },
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();

  if (!entities.fzart) return null;

  const items = Object.values(entities.fzart);

  const vehiclesCount = items.length;

  const setValueHandler = (value: any) => {
    // Check if all variants are selected
    const newValue = value.length === vehiclesCount ? [] : value;
    dispatchFilter({ type: "UPDATE_FILTER", payload: { fzart: newValue } });
  };

  const options = items.map(({ id, name }) => ({
    value: id,
    title: t({ id: `fzart.${id}.label` }, { defaultMessage: name }),
  }));

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  const Component = (
    <Box sx={{ width: 1 }}>
      <List {...{ value, options, setValueHandler, all }} />
    </Box>
  );

  return (
    <Filter
      component={Component}
      title={
        <FormattedMessage id="filter.fzart.label" defaultMessage="Fzart" />
      }
    />
  );
};

export const SimpleFilter: React.FC<IFilter> = ({
  entities = {},
  value = [],
  dispatchFilter = () => null,
}): JSX.Element | null => {
  const { formatMessage: t } = useIntl();
  const { state: vehicles } = React.useContext(VehicleStore);

  const setValueHandler = (event: SelectChangeEvent) => {
    const value = parseInt(event.target.value);
    // Check if all variants are selected
    const newValue = value === 0 ? [] : [value];
    dispatchFilter({
      type: "UPDATE_FILTER",
      payload: { fzart: newValue },
    });
  };

  if (!(entities.fzart && vehicles)) return null;

  const items = Object.values(entities.fzart);

  const options = items
    .map(({ id, name }) => ({
      value: id,
      title: t({ id: `fzart.${id}.label`, defaultMessage: name }),
    }))
    .sort(sortAsc);

  const all = {
    title: t({ id: "common.alle", defaultMessage: "Alle" }),
  };

  return (
    <FormControl size="small">
      <InputLabel id="filter-fzart-simple-label">
        <FormattedMessage id="filter.fzart.label" defaultMessage="fzart" />
      </InputLabel>
      <Select
        labelId="filter-fzart-simple-label"
        id="filter-fzart-simple"
        value={value.length ? value[0].toString() : "0"}
        label={t({
          id: "filter.fzart.label",
          defaultMessage: "fzart",
        })}
        onChange={setValueHandler}
      >
        <MenuItem value={0}>{all.title}</MenuItem>
        {options.map(({ value, title }) => (
          <MenuItem key={"fzart-" + value} value={value}>
            {title}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

interface IContainer {
  Pipe?: any;
  variant?: string;
}

const Container: React.FC<IContainer> = ({
  Pipe = Pipeline,
  variant = "side",
}) => {
  const Filter = variant === "side" ? SideFilter : SimpleFilter;
  return (
    <Pipe
      {...{
        type: "fzart",
        defaultValue: [],
        Component: Filter,
        Reducer: FzartReducer,
        Indicator: FzartIndicator,
      }}
    />
  );
};

export default Container;
